import { useEffect, useRef, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import AdminLayout from '../components/AdminLayout';
import { defaultSiteContent, SiteContent, StatItem, FeatureItem, TestimonialItem, AnimationCard, AnimationMediaMode } from '@/mocks/siteContent';
import { useSiteContent } from '@/context/SiteContentContext';
import { useProductLaunch, defaultLaunch, ProductLaunch } from '@/context/ProductLaunchContext';
import { uploadAnimationMedia, uploadContentMedia } from '@/lib/api/siteContent';
import WikiAdminTab from './WikiAdminTab';
import LegalAdminTab from './LegalAdminTab';
import FaqAdminTab from './FaqAdminTab';

type Tab = 'hero' | 'about' | 'stats' | 'features' | 'testimonials' | 'footer' | 'launch' | 'wiki' | 'legal' | 'faq' | 'animation';

type AnimationPreviewState = {
  type: Exclude<AnimationMediaMode, 'animation'>;
  url: string;
};

const MAX_ANIMATION_IMAGE_UPLOAD_BYTES = 2 * 1024 * 1024;
const MAX_ANIMATION_IMAGE_DIMENSION = 2200;

async function loadImageDimensions(file: File) {
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const nextImage = new Image();
      nextImage.onload = () => resolve(nextImage);
      nextImage.onerror = () => reject(new Error('Unable to read the selected image.'));
      nextImage.src = objectUrl;
    });

    return {
      width: image.naturalWidth,
      height: image.naturalHeight,
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function optimizeAnimationImage(file: File) {
  if (!file.type.startsWith('image/') || file.type === 'image/gif') {
    return file;
  }

  const { width, height } = await loadImageDimensions(file);
  const longestSide = Math.max(width, height);
  const needsResize = longestSide > MAX_ANIMATION_IMAGE_DIMENSION;
  const needsCompression = file.size > MAX_ANIMATION_IMAGE_UPLOAD_BYTES;

  if (!needsResize && !needsCompression) {
    return file;
  }

  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const nextImage = new Image();
      nextImage.onload = () => resolve(nextImage);
      nextImage.onerror = () => reject(new Error('Unable to process the selected image.'));
      nextImage.src = objectUrl;
    });

    const scale = Math.min(1, MAX_ANIMATION_IMAGE_DIMENSION / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));

    const context = canvas.getContext('2d');
    if (!context) {
      return file;
    }

    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/webp', 0.86);
    });

    if (!blob || blob.size === 0 || blob.size >= file.size) {
      return file;
    }

    const nextName = file.name.replace(/\.[^.]+$/, '') || 'animation-media';
    return new File([blob], `${nextName}.webp`, {
      type: 'image/webp',
      lastModified: Date.now(),
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function ContentManagementInner() {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('hero');
  const { content: globalContent, updateContent, resetContent } = useSiteContent();
  const { launch: globalLaunch, updateLaunch } = useProductLaunch();
  const [content, setContent] = useState<SiteContent>(globalContent);
  const [launch, setLaunch] = useState<ProductLaunch>(globalLaunch);
  const [saved, setSaved] = useState(false);
  const [launchSaved, setLaunchSaved] = useState(false);
  const [heroImageUploadError, setHeroImageUploadError] = useState('');
  const [heroImageUploading, setHeroImageUploading] = useState<'imageDark' | 'imageLight' | null>(null);
  const [animationUploadError, setAnimationUploadError] = useState('');
  const [animationUploading, setAnimationUploading] = useState(false);
  const [animationPreview, setAnimationPreview] = useState<AnimationPreviewState | null>(null);
  const animationPreviewUrlRef = useRef<string | null>(null);

  const card = dark ? 'bg-[#1C2B3A] border border-white/5' : 'bg-white border border-gray-200';
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/50' : 'text-gray-500';
  const inputCls = dark
    ? 'bg-[#0D1B2A] border-white/10 text-white placeholder-white/30 focus:border-[#F97316]'
    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#F97316]';
  const labelCls = `block text-xs font-semibold uppercase tracking-wide mb-1.5 ${muted}`;
  const fieldCls = `w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`;
  const animationPreviewMode = animationPreview?.type ?? content.animation.mediaMode;
  const animationPreviewUrl = animationPreview?.url ?? content.animation.mediaUrl;

  useEffect(() => {
    return () => {
      if (animationPreviewUrlRef.current) {
        URL.revokeObjectURL(animationPreviewUrlRef.current);
      }
    };
  }, []);

  const clearAnimationPreview = () => {
    if (animationPreviewUrlRef.current) {
      URL.revokeObjectURL(animationPreviewUrlRef.current);
      animationPreviewUrlRef.current = null;
    }
    setAnimationPreview(null);
  };

  const setAnimationLocalPreview = (file: File) => {
    clearAnimationPreview();

    const previewUrl = URL.createObjectURL(file);
    animationPreviewUrlRef.current = previewUrl;
    setAnimationPreview({
      type: file.type.startsWith('video/') ? 'video' : 'image',
      url: previewUrl,
    });
  };

  const tabs: { key: Tab; label: string; icon: string; highlight?: boolean }[] = [
    { key: 'hero', label: 'Hero Section', icon: 'ri-home-4-line' },
    { key: 'about', label: 'About Section', icon: 'ri-information-line' },
    { key: 'stats', label: 'Stats', icon: 'ri-bar-chart-line' },
    { key: 'features', label: 'Features', icon: 'ri-star-line' },
    { key: 'testimonials', label: 'Testimonials', icon: 'ri-chat-quote-line' },
    { key: 'footer', label: 'Footer', icon: 'ri-layout-bottom-line' },
    { key: 'launch', label: 'Product Launch', icon: 'ri-rocket-line', highlight: true },
    { key: 'animation', label: 'Animation', icon: 'ri-movie-line', highlight: true },
    { key: 'wiki', label: 'Tech Wiki', icon: 'ri-book-2-line', highlight: true },
    { key: 'legal', label: 'Legal Pages', icon: 'ri-shield-line', highlight: true },
    { key: 'faq', label: 'FAQ', icon: 'ri-question-answer-line', highlight: true },
  ];

  const setL = <K extends keyof ProductLaunch>(key: K, value: ProductLaunch[K]) =>
    setLaunch((prev) => ({ ...prev, [key]: value }));

  const updateSpec = (i: number, field: 'label' | 'value', val: string) =>
    setLaunch((prev) => {
      const specs = [...prev.specs];
      specs[i] = { ...specs[i], [field]: val };
      return { ...prev, specs };
    });

  const addSpec = () =>
    setLaunch((prev) => ({ ...prev, specs: [...prev.specs, { label: '', value: '' }] }));

  const removeSpec = (i: number) =>
    setLaunch((prev) => ({ ...prev, specs: prev.specs.filter((_, idx) => idx !== i) }));

  const handleSaveLaunch = () => {
    updateLaunch(launch);
    setLaunchSaved(true);
    setTimeout(() => setLaunchSaved(false), 2500);
  };

  const handleSave = () => {
    updateContent(content);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleReset = () => {
    resetContent();
    setContent(defaultSiteContent);
  };

  const updateHero = (key: keyof typeof content.hero, val: string) => {
    setContent((prev) => ({ ...prev, hero: { ...prev.hero, [key]: val } }));
  };

  const handleHeroImageUpload = async (key: 'imageDark' | 'imageLight', file: File | null) => {
    setHeroImageUploadError('');

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      setHeroImageUploadError('Please choose a valid image file.');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      setHeroImageUploadError('Image must be smaller than 50 MB.');
      return;
    }

    setHeroImageUploading(key);

    try {
      const uploaded = await uploadContentMedia(file);
      setContent((prev) => ({
        ...prev,
        hero: {
          ...prev.hero,
          [key]: uploaded.url,
        },
      }));
    } catch (err) {
      setHeroImageUploadError(err instanceof Error ? err.message : 'Unable to upload hero image.');
    } finally {
      setHeroImageUploading(null);
    }
  };

  const updateAbout = (key: keyof typeof content.about, val: string) => {
    setContent((prev) => ({ ...prev, about: { ...prev.about, [key]: val } }));
  };

  const updateFooter = (key: keyof typeof content.footer, val: string) => {
    setContent((prev) => ({ ...prev, footer: { ...prev.footer, [key]: val } }));
  };

  const updateStat = (i: number, key: keyof StatItem, val: string) => {
    setContent((prev) => {
      const stats = [...prev.stats];
      stats[i] = { ...stats[i], [key]: val };
      return { ...prev, stats };
    });
  };

  const updateFeature = (i: number, key: keyof FeatureItem, val: string) => {
    setContent((prev) => {
      const features = [...prev.features];
      features[i] = { ...features[i], [key]: val };
      return { ...prev, features };
    });
  };

  const updateAnimField = (key: keyof typeof content.animation, val: string | boolean) =>
    setContent((prev) => ({ ...prev, animation: { ...prev.animation, [key]: val } }));

  const updateAnimCard = (i: number, key: keyof AnimationCard, val: string | boolean) =>
    setContent((prev) => {
      const cards = [...prev.animation.cards];
      cards[i] = { ...cards[i], [key]: val };
      return { ...prev, animation: { ...prev.animation, cards } };
    });

  const addAnimCard = () =>
    setContent((prev) => ({
      ...prev,
      animation: {
        ...prev.animation,
        cards: [...prev.animation.cards, { enabled: true, icon: 'ri-star-line', title: 'New Feature', desc: 'Description here.' }],
      },
    }));

  const removeAnimCard = (i: number) =>
    setContent((prev) => ({
      ...prev,
      animation: { ...prev.animation, cards: prev.animation.cards.filter((_, idx) => idx !== i) },
    }));

  const persistAnimationToggle = (key: 'enabled' | 'animationEnabled' | 'cardsEnabled') => {
    const nextAnimation = { ...content.animation, [key]: !content.animation[key] };
    const nextContent = { ...content, animation: nextAnimation };
    setContent(nextContent);
    updateContent(nextContent);
  };

  const persistAnimationCardToggle = (index: number) => {
    const cards = [...content.animation.cards];
    const current = cards[index];
    cards[index] = { ...current, enabled: !current.enabled };
    const nextContent = {
      ...content,
      animation: {
        ...content.animation,
        cards,
      },
    };
    setContent(nextContent);
    updateContent(nextContent);
  };

  const handleAnimationMediaUpload = async (file: File | null) => {
    setAnimationUploadError('');

    if (!file) {
      clearAnimationPreview();
      return;
    }

    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');

    if (!isImage && !isVideo) {
      setAnimationUploadError('Please choose a valid image or video file.');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      setAnimationUploadError('Media must be smaller than 50 MB.');
      return;
    }

    let uploadFile = file;

    if (isImage) {
      try {
        uploadFile = await optimizeAnimationImage(file);
      } catch (err) {
        setAnimationUploadError(err instanceof Error ? err.message : 'Unable to prepare the selected image.');
        return;
      }
    }

    setAnimationLocalPreview(uploadFile);
    setAnimationUploading(true);

    try {
      const uploaded = await uploadAnimationMedia(uploadFile);
      setContent((prev) => ({
        ...prev,
        animation: {
          ...prev.animation,
          mediaMode: uploaded.type,
          mediaUrl: uploaded.url,
        },
      }));
      clearAnimationPreview();
    } catch (err) {
      setAnimationUploadError(err instanceof Error ? err.message : 'Unable to upload media.');
    } finally {
      setAnimationUploading(false);
    }
  };

  const updateTestimonial = (i: number, key: keyof TestimonialItem, val: string) => {
    setContent((prev) => {
      const testimonials = [...prev.testimonials];
      testimonials[i] = { ...testimonials[i], [key]: val };
      return { ...prev, testimonials };
    });
  };

  const addTestimonial = () => {
    setContent((prev) => ({
      ...prev,
      testimonials: [...prev.testimonials, { name: 'New Customer', role: 'Role, Location', text: 'Enter testimonial text here.', initials: 'NC' }],
    }));
  };

  const removeTestimonial = (i: number) => {
    setContent((prev) => ({ ...prev, testimonials: prev.testimonials.filter((_, idx) => idx !== i) }));
  };

  return (
    <div className="space-y-5">
      {/* Tabs */}
      <div className={`flex flex-wrap gap-1 p-1 rounded-lg ${dark ? 'bg-white/5' : 'bg-gray-100'}`}>
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              tab === t.key
                ? 'bg-[#F97316] text-white'
                : t.highlight
                ? dark ? 'text-[#F97316]/80 hover:text-[#F97316] hover:bg-[#F97316]/10' : 'text-[#F97316] hover:bg-[#F97316]/10'
                : dark ? 'text-white/50 hover:text-white' : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={t.icon}></i>
            </div>
            {t.label}
            {t.highlight && tab !== t.key && (
              <span className="w-1.5 h-1.5 rounded-full bg-[#F97316] ml-0.5"></span>
            )}
          </button>
        ))}
      </div>

      {/* Hero */}
      {tab === 'hero' && (
        <div className={`${card} rounded-lg p-6 space-y-4`}>
          <h2 className={`font-bold text-sm mb-2 ${text}`}>Hero Section Content</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Badge Text</label>
              <input className={fieldCls} value={content.hero.badge} onChange={(e) => updateHero('badge', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>CTA Button 1</label>
              <input className={fieldCls} value={content.hero.cta1} onChange={(e) => updateHero('cta1', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Headline Line 1</label>
              <input className={fieldCls} value={content.hero.headline1} onChange={(e) => updateHero('headline1', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Headline Line 2 (Orange)</label>
              <input className={fieldCls} value={content.hero.headline2} onChange={(e) => updateHero('headline2', e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Subtext</label>
            <textarea rows={3} className={fieldCls} value={content.hero.subtext} onChange={(e) => updateHero('subtext', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>CTA Button 2</label>
            <input className={fieldCls} value={content.hero.cta2} onChange={(e) => updateHero('cta2', e.target.value)} />
          </div>
          {/* Hero Images */}
          <div className={`rounded-lg p-5 border ${dark ? 'border-white/10 bg-[#0D1B2A]' : 'border-gray-100 bg-gray-50'}`}>
            <p className={`text-xs font-bold uppercase tracking-widest mb-4 ${muted}`}>
              <i className="ri-image-line mr-1.5 text-[#F97316]"></i>
              Background Images (by Theme)
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Dark mode image */}
              <div>
                <label className={labelCls}>Dark Mode Image URL</label>
                <input
                  className={fieldCls}
                  value={content.hero.imageDark}
                  onChange={(e) => updateHero('imageDark', e.target.value)}
                  placeholder="https://..."
                />
                <div className={`mt-3 rounded-lg border p-3 ${dark ? 'border-white/10 bg-[#07101C]' : 'border-gray-200 bg-white'}`}>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={(e) => void handleHeroImageUpload('imageDark', e.target.files?.[0] ?? null)}
                    className={`block w-full text-sm ${dark ? 'text-white/70 file:bg-white/10 file:text-white' : 'text-gray-600 file:bg-white file:text-gray-700'} file:mr-3 file:rounded-md file:border-0 file:px-3 file:py-2 file:text-sm file:font-semibold`}
                  />
                  <p className={`mt-2 text-xs ${muted}`}>Upload a dark-mode hero image. The uploaded file will fill the URL field automatically.</p>
                  {heroImageUploading === 'imageDark' && (
                    <p className="mt-2 text-xs font-semibold" style={{ color: 'var(--accent)' }}>Uploading dark-mode image...</p>
                  )}
                </div>
                {content.hero.imageDark && (
                  <div className="mt-2 w-full h-28 rounded-lg overflow-hidden border border-white/10">
                    <img src={content.hero.imageDark} alt="Dark mode preview" className="w-full h-full object-cover object-top" />
                  </div>
                )}
                <p className={`text-xs mt-1.5 ${muted}`}>Shown when site is in dark mode</p>
              </div>
              {/* Light mode image */}
              <div>
                <label className={labelCls}>Light Mode Image URL</label>
                <input
                  className={fieldCls}
                  value={content.hero.imageLight}
                  onChange={(e) => updateHero('imageLight', e.target.value)}
                  placeholder="https://..."
                />
                <div className={`mt-3 rounded-lg border p-3 ${dark ? 'border-white/10 bg-[#07101C]' : 'border-gray-200 bg-white'}`}>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={(e) => void handleHeroImageUpload('imageLight', e.target.files?.[0] ?? null)}
                    className={`block w-full text-sm ${dark ? 'text-white/70 file:bg-white/10 file:text-white' : 'text-gray-600 file:bg-white file:text-gray-700'} file:mr-3 file:rounded-md file:border-0 file:px-3 file:py-2 file:text-sm file:font-semibold`}
                  />
                  <p className={`mt-2 text-xs ${muted}`}>Upload a light-mode hero image. The uploaded file will fill the URL field automatically.</p>
                  {heroImageUploading === 'imageLight' && (
                    <p className="mt-2 text-xs font-semibold" style={{ color: 'var(--accent)' }}>Uploading light-mode image...</p>
                  )}
                </div>
                {content.hero.imageLight && (
                  <div className="mt-2 w-full h-28 rounded-lg overflow-hidden border border-gray-200">
                    <img src={content.hero.imageLight} alt="Light mode preview" className="w-full h-full object-cover object-top" />
                  </div>
                )}
                <p className={`text-xs mt-1.5 ${muted}`}>Shown when site is in light mode</p>
              </div>
            </div>
            {heroImageUploadError && (
              <div className="mt-4 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">
                {heroImageUploadError}
              </div>
            )}
          </div>

          {/* Preview */}
          <div className={`rounded-lg p-5 mt-2 ${dark ? 'bg-[#0D1B2A]' : 'bg-gray-50'}`}>
            <p className={`text-xs font-bold uppercase tracking-widest mb-3 ${muted}`}>Text Preview</p>
            <span className="inline-block bg-[#F97316]/20 border border-[#F97316]/40 text-[#F97316] text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-3">
              {content.hero.badge}
            </span>
            <h3 className={`text-2xl font-black ${text} leading-tight`}>
              {content.hero.headline1}<br />
              <span className="text-[#F97316]">{content.hero.headline2}</span>
            </h3>
            <p className={`text-sm mt-2 ${muted}`}>{content.hero.subtext}</p>
          </div>
        </div>
      )}

      {/* About */}
      {tab === 'about' && (
        <div className={`${card} rounded-lg p-6 space-y-4`}>
          <h2 className={`font-bold text-sm mb-2 ${text}`}>About Section Content</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Badge Text</label>
              <input className={fieldCls} value={content.about.badge} onChange={(e) => updateAbout('badge', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>CTA Button Label</label>
              <input className={fieldCls} value={content.about.ctaLabel} onChange={(e) => updateAbout('ctaLabel', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Headline Line 1</label>
              <input className={fieldCls} value={content.about.headline1} onChange={(e) => updateAbout('headline1', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Headline Line 2 (Muted)</label>
              <input className={fieldCls} value={content.about.headline2} onChange={(e) => updateAbout('headline2', e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Paragraph 1</label>
            <textarea rows={3} className={fieldCls} value={content.about.paragraph1} onChange={(e) => updateAbout('paragraph1', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Paragraph 2</label>
            <textarea rows={3} className={fieldCls} value={content.about.paragraph2} onChange={(e) => updateAbout('paragraph2', e.target.value)} />
          </div>
        </div>
      )}

      {/* Stats */}
      {tab === 'stats' && (
        <div className={`${card} rounded-lg p-6`}>
          <h2 className={`font-bold text-sm mb-4 ${text}`}>Stats Bar (4 items)</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {content.stats.map((s, i) => (
              <div key={i} className={`rounded-lg p-4 ${dark ? 'bg-[#0D1B2A]' : 'bg-gray-50'}`}>
                <p className={`text-xs font-bold uppercase tracking-widest mb-3 text-[#F97316]`}>Stat {i + 1}</p>
                <div className="space-y-2">
                  <div>
                    <label className={labelCls}>Value</label>
                    <input className={fieldCls} value={s.value} onChange={(e) => updateStat(i, 'value', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>Label</label>
                    <input className={fieldCls} value={s.label} onChange={(e) => updateStat(i, 'label', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>Sub Label</label>
                    <input className={fieldCls} value={s.sub} onChange={(e) => updateStat(i, 'sub', e.target.value)} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Features */}
      {tab === 'features' && (
        <div className={`${card} rounded-lg p-6`}>
          <h2 className={`font-bold text-sm mb-4 ${text}`}>Feature Strip (4 items)</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {content.features.map((f, i) => (
              <div key={i} className={`rounded-lg p-4 ${dark ? 'bg-[#0D1B2A]' : 'bg-gray-50'}`}>
                <p className={`text-xs font-bold uppercase tracking-widest mb-3 text-[#F97316]`}>Feature {i + 1}</p>
                <div className="space-y-2">
                  <div>
                    <label className={labelCls}>Icon Class (Remix Icon)</label>
                    <div className="flex gap-2 items-center">
                      <input className={`${fieldCls} flex-1`} value={f.icon} onChange={(e) => updateFeature(i, 'icon', e.target.value)} />
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#F97316]/20 shrink-0">
                        <i className={`${f.icon} text-[#F97316] text-lg`}></i>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Title</label>
                    <input className={fieldCls} value={f.title} onChange={(e) => updateFeature(i, 'title', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>Description</label>
                    <input className={fieldCls} value={f.desc} onChange={(e) => updateFeature(i, 'desc', e.target.value)} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Testimonials */}
      {tab === 'testimonials' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className={`font-bold text-sm ${text}`}>Testimonials ({content.testimonials.length})</h2>
            <button
              onClick={addTestimonial}
              className="flex items-center gap-2 bg-[#F97316] text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-[#ea6a0a] transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line"></i>
              </div>
              Add Testimonial
            </button>
          </div>
          {content.testimonials.map((t, i) => (
            <div key={i} className={`${card} rounded-lg p-5`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F97316] text-white font-black text-xs">
                    {t.initials}
                  </div>
                  <span className={`text-sm font-bold ${text}`}>Testimonial {i + 1}</span>
                </div>
                <button
                  onClick={() => removeTestimonial(i)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer"
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className={labelCls}>Name</label>
                  <input className={fieldCls} value={t.name} onChange={(e) => updateTestimonial(i, 'name', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Initials (2 chars)</label>
                  <input className={fieldCls} maxLength={2} value={t.initials} onChange={(e) => updateTestimonial(i, 'initials', e.target.value)} />
                </div>
              </div>
              <div className="mb-3">
                <label className={labelCls}>Role / Location</label>
                <input className={fieldCls} value={t.role} onChange={(e) => updateTestimonial(i, 'role', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Testimonial Text</label>
                <textarea rows={2} className={fieldCls} value={t.text} onChange={(e) => updateTestimonial(i, 'text', e.target.value)} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      {tab === 'footer' && (
        <div className={`${card} rounded-lg p-6 space-y-4`}>
          <h2 className={`font-bold text-sm mb-2 ${text}`}>Footer Content</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Phone Number</label>
              <input className={fieldCls} value={content.footer.phone} onChange={(e) => updateFooter('phone', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Email Address</label>
              <input className={fieldCls} value={content.footer.email} onChange={(e) => updateFooter('email', e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Copyright Text</label>
            <input className={fieldCls} value={content.footer.copyright} onChange={(e) => updateFooter('copyright', e.target.value)} />
          </div>
          {/* Preview */}
          <div className={`rounded-lg p-4 mt-2 ${dark ? 'bg-[#0A0F1A]' : 'bg-gray-900'}`}>
            <p className="text-white/25 text-xs text-center">
              &copy; {new Date().getFullYear()} {content.footer.copyright}
            </p>
          </div>
        </div>
      )}

      {/* Product Launch */}
      {tab === 'launch' && (
        <div className="space-y-5">
          {/* Enable toggle */}
          <div className={`${card} rounded-lg p-5 flex items-center justify-between`}>
            <div>
              <p className={`font-bold text-sm ${text}`}>Product Launch Section</p>
              <p className={`text-xs mt-0.5 ${muted}`}>When enabled, a dedicated launch announcement section appears on the homepage.</p>
            </div>
            <button
              onClick={() => setL('enabled', !launch.enabled)}
              className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${launch.enabled ? 'bg-[#F97316]' : dark ? 'bg-white/20' : 'bg-gray-200'}`}
            >
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${launch.enabled ? 'left-7' : 'left-1'}`}></span>
            </button>
          </div>

          <div className={`${card} rounded-lg p-6 space-y-4`}>
            <h2 className={`font-bold text-sm ${text} flex items-center gap-2`}>
              <i className="ri-rocket-line text-[#F97316]"></i>
              Launch Details
            </h2>

            {/* Name + Badge */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Product Name *</label>
                <input className={fieldCls} value={launch.productName} onChange={(e) => setL('productName', e.target.value)} placeholder="Stingray LiFePO4 300Ah" />
              </div>
              <div>
                <label className={labelCls}>Badge Label</label>
                <input className={fieldCls} value={launch.badge} onChange={(e) => setL('badge', e.target.value)} placeholder="Coming Soon" />
              </div>
            </div>

            {/* Tagline */}
            <div>
              <label className={labelCls}>Tagline (accent color)</label>
              <input className={fieldCls} value={launch.tagline} onChange={(e) => setL('tagline', e.target.value)} placeholder="The most powerful battery we've ever built." />
            </div>

            {/* Description */}
            <div>
              <label className={labelCls}>Description</label>
              <textarea rows={3} className={fieldCls} value={launch.description} onChange={(e) => setL('description', e.target.value)} />
            </div>

            {/* Launch date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Launch Date & Time</label>
                <input
                  type="datetime-local"
                  className={fieldCls}
                  value={launch.launchDate ? launch.launchDate.slice(0, 16) : ''}
                  onChange={(e) => setL('launchDate', new Date(e.target.value).toISOString())}
                />
              </div>
              <div>
                <label className={labelCls}>Accent Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={launch.accentColor}
                    onChange={(e) => setL('accentColor', e.target.value)}
                    className="w-10 h-10 rounded-lg cursor-pointer border-0 bg-transparent"
                  />
                  <input className={`${fieldCls} flex-1`} value={launch.accentColor} onChange={(e) => setL('accentColor', e.target.value)} placeholder="#F97316" />
                </div>
              </div>
            </div>

            {/* Product image */}
            <div>
              <label className={labelCls}>Product Image URL</label>
              <input className={fieldCls} value={launch.image} onChange={(e) => setL('image', e.target.value)} placeholder="https://..." />
              {launch.image && (
                <div className="mt-2 w-full h-36 rounded-lg overflow-hidden border border-white/10">
                  <img src={launch.image} alt="Launch product" className="w-full h-full object-cover object-top" />
                </div>
              )}
            </div>

            {/* CTA */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>CTA Button Label</label>
                <input className={fieldCls} value={launch.ctaLabel} onChange={(e) => setL('ctaLabel', e.target.value)} placeholder="Notify Me at Launch" />
              </div>
              <div>
                <label className={labelCls}>CTA URL (after launch)</label>
                <input className={fieldCls} value={launch.ctaUrl} onChange={(e) => setL('ctaUrl', e.target.value)} placeholder="/products/sr-li-300" />
              </div>
            </div>

            {/* Options */}
            <div className={`rounded-lg p-4 border ${dark ? 'border-white/10 bg-[#0D1B2A]/60' : 'border-gray-100 bg-gray-50'} space-y-3`}>
              <p className={`text-xs font-bold uppercase tracking-widest ${muted}`}>Options</p>
              {[
                { key: 'showCountdown' as const, label: 'Show Countdown Timer', desc: 'Live countdown to launch date' },
                { key: 'showNotifyForm' as const, label: 'Show Email Notify Form', desc: 'Collect emails before launch' },
              ].map((opt) => (
                <div key={opt.key} className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm font-medium ${text}`}>{opt.label}</p>
                    <p className={`text-xs ${muted}`}>{opt.desc}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setL(opt.key, !launch[opt.key])}
                    className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${launch[opt.key] ? 'bg-[#F97316]' : dark ? 'bg-white/20' : 'bg-gray-200'}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${launch[opt.key] ? 'left-5' : 'left-0.5'}`}></span>
                  </button>
                </div>
              ))}
            </div>

            {/* Specs */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className={labelCls}>Key Specs (shown as cards)</label>
                <button onClick={addSpec} className="flex items-center gap-1 text-xs font-bold text-[#F97316] hover:underline cursor-pointer whitespace-nowrap">
                  <i className="ri-add-line"></i> Add Spec
                </button>
              </div>
              <div className="space-y-2">
                {launch.specs.map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input className={`${fieldCls} flex-1`} value={s.value} onChange={(e) => updateSpec(i, 'value', e.target.value)} placeholder="300Ah" />
                    <input className={`${fieldCls} flex-1`} value={s.label} onChange={(e) => updateSpec(i, 'label', e.target.value)} placeholder="Capacity" />
                    <button onClick={() => removeSpec(i)} className="w-8 h-8 flex items-center justify-center rounded-lg text-red-400 hover:bg-red-500/10 cursor-pointer shrink-0">
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Save launch */}
          <div className="flex items-center justify-end gap-3">
            {launchSaved && (
              <div className="flex items-center gap-2 text-green-400 text-sm font-semibold">
                <i className="ri-check-line"></i>
                Launch page saved!
              </div>
            )}
            <button
              onClick={handleSaveLaunch}
              className="flex items-center gap-2 bg-[#F97316] text-white font-bold px-6 py-2.5 rounded-lg hover:bg-[#ea6a0a] transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-save-line"></i>
              </div>
              Save Launch Page
            </button>
          </div>
        </div>
      )}

      {/* Tech Wiki */}
      {tab === 'wiki' && <WikiAdminTab />}

      {/* Legal Pages */}
      {tab === 'legal' && <LegalAdminTab />}

      {/* FAQ */}
      {tab === 'faq' && <FaqAdminTab />}

      {/* Animation */}
      {tab === 'animation' && (
        <div className="space-y-5">
          {/* Enable toggles */}
          <div className={`${card} rounded-lg p-5 space-y-4`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className={`font-bold text-sm ${text}`}>Stingray Animation Section</p>
                <p className={`text-xs mt-0.5 ${muted}`}>About sayfasındaki bu alanı artık bölüm bölüm açıp kapatabilirsiniz.</p>
              </div>
            </div>

            {[
              {
                key: 'enabled' as const,
                title: 'Entire Section',
                desc: 'Tüm stingray animation alanını About sayfasında gösterir ya da gizler.',
                value: content.animation.enabled,
              },
              {
                key: 'animationEnabled' as const,
                title: 'Animation Canvas',
                desc: 'Ortadaki animasyonun kendisini tek başına açıp kapatır.',
                value: content.animation.animationEnabled,
              },
              {
                key: 'cardsEnabled' as const,
                title: 'Info Cards Group',
                desc: 'Animasyonun altındaki bilgi kartı grubunu topluca açıp kapatır.',
                value: content.animation.cardsEnabled,
              },
            ].map((toggle) => (
              <div
                key={toggle.key}
                className={`flex items-center justify-between gap-4 rounded-lg border px-4 py-3 ${dark ? 'border-white/10 bg-[#0D1B2A]' : 'border-gray-200 bg-gray-50'}`}
              >
                <div>
                  <p className={`text-sm font-semibold ${text}`}>{toggle.title}</p>
                  <p className={`text-xs mt-0.5 ${muted}`}>{toggle.desc}</p>
                </div>
                <button
                  onClick={() => persistAnimationToggle(toggle.key)}
                  className="relative w-12 h-6 rounded-full transition-colors cursor-pointer shrink-0"
                  style={{ backgroundColor: toggle.value ? 'var(--accent)' : dark ? 'rgba(255,255,255,0.2)' : '#e5e7eb' }}
                >
                  <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${toggle.value ? 'left-7' : 'left-1'}`}></span>
                </button>
              </div>
            ))}
          </div>

          <div className={`${card} rounded-lg p-6 space-y-4`}>
            <h2 className={`font-bold text-sm ${text} flex items-center gap-2`}>
              <i className="ri-movie-line" style={{ color: 'var(--accent)' }}></i>
              Section Text
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Badge Label</label>
                <input className={fieldCls} value={content.animation.badge} onChange={(e) => updateAnimField('badge', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Headline Line 1</label>
                <input className={fieldCls} value={content.animation.headline1} onChange={(e) => updateAnimField('headline1', e.target.value)} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Headline Line 2 (Accent Color)</label>
              <input className={fieldCls} value={content.animation.headline2} onChange={(e) => updateAnimField('headline2', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Subtitle Text</label>
              <textarea rows={2} className={fieldCls} value={content.animation.subtitle} onChange={(e) => updateAnimField('subtitle', e.target.value)} />
            </div>
          </div>

          <div className={`${card} rounded-lg p-6 space-y-4`}>
            <h2 className={`font-bold text-sm ${text} flex items-center gap-2`}>
              <i className="ri-gallery-line" style={{ color: 'var(--accent)' }}></i>
              Animation Canvas Media
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Canvas Source</label>
                <select
                  className={fieldCls}
                  value={content.animation.mediaMode}
                  onChange={(e) => {
                    const nextMode = e.target.value as AnimationMediaMode;
                    if (nextMode === 'animation') {
                      clearAnimationPreview();
                    }
                    updateAnimField('mediaMode', nextMode);
                  }}
                >
                  <option value="animation">Code Animation</option>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Media URL</label>
                <input
                  className={fieldCls}
                  value={content.animation.mediaUrl}
                  onChange={(e) => {
                    clearAnimationPreview();
                    updateAnimField('mediaUrl', e.target.value);
                  }}
                  placeholder="/uploads/content/your-file.mp4"
                />
              </div>
            </div>

            <div className={`rounded-lg border p-4 ${dark ? 'border-white/10 bg-[#0D1B2A]' : 'border-gray-200 bg-gray-50'}`}>
              <label className={labelCls}>Upload Replacement Media</label>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,video/ogg,video/quicktime"
                onChange={(e) => void handleAnimationMediaUpload(e.target.files?.[0] ?? null)}
                className={`block w-full text-sm ${dark ? 'text-white/70 file:bg-white/10 file:text-white' : 'text-gray-600 file:bg-white file:text-gray-700'} file:mr-3 file:rounded-md file:border-0 file:px-3 file:py-2 file:text-sm file:font-semibold`}
              />
              <p className={`mt-2 text-xs ${muted}`}>
                Upload an image or video to replace the code animation on the About page. Supported: JPG, PNG, WEBP, GIF, MP4, WEBM, OGG, MOV. Max 50 MB.
              </p>
              {animationUploadError && (
                <div className="mt-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">
                  {animationUploadError}
                </div>
              )}
              {animationUploading && (
                <p className="mt-3 text-xs font-semibold" style={{ color: 'var(--accent)' }}>
                  Uploading media...
                </p>
              )}
              {(content.animation.mediaMode !== 'animation' || content.animation.mediaUrl) && (
                <button
                  type="button"
                  onClick={() => {
                    clearAnimationPreview();
                    setContent((prev) => ({
                      ...prev,
                      animation: {
                        ...prev.animation,
                        mediaMode: 'animation',
                        mediaUrl: '',
                      },
                    }));
                  }}
                  className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-[#F97316] hover:underline"
                >
                  <i className="ri-refresh-line"></i>
                  Use code animation instead
                </button>
              )}
            </div>

            <div>
              <label className={labelCls}>Canvas Preview</label>
              <div className={`rounded-2xl overflow-hidden border ${dark ? 'border-white/10 bg-[#071525]' : 'border-gray-200 bg-white'}`} style={{ minHeight: 260 }}>
                {animationPreviewMode === 'image' && animationPreviewUrl ? (
                  <img
                    src={animationPreviewUrl}
                    alt="Animation canvas preview"
                    className="w-full h-[320px] object-cover object-center"
                  />
                ) : animationPreviewMode === 'video' && animationPreviewUrl ? (
                  <video
                    src={animationPreviewUrl}
                    className="w-full h-[320px] object-cover object-center"
                    controls
                    muted
                    playsInline
                  />
                ) : (
                  <div className={`h-[260px] flex flex-col items-center justify-center text-center px-6 ${muted}`}>
                    <i className="ri-movie-2-line text-3xl mb-3"></i>
                    <p className={`text-sm font-semibold ${text}`}>Code animation will be used</p>
                    <p className="text-xs mt-1">Upload an image or video if you want to replace the live stingray animation canvas.</p>
                  </div>
                )}
              </div>
              {animationPreview && (
                <p className={`mt-2 text-xs ${muted}`}>
                  Local preview is shown immediately while the media is being uploaded.
                </p>
              )}
            </div>
          </div>

          {/* Cards */}
          <div className={`${card} rounded-lg p-6`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`font-bold text-sm ${text}`}>
                Feature Cards ({content.animation.cards.filter((cardItem) => cardItem.enabled).length}/{content.animation.cards.length} enabled)
              </h2>
              <button
                onClick={addAnimCard}
                className="flex items-center gap-2 text-white text-xs font-bold px-4 py-2 rounded-lg cursor-pointer whitespace-nowrap"
                style={{ backgroundColor: 'var(--accent)' }}
              >
                <i className="ri-add-line"></i> Add Card
              </button>
            </div>
            <div className="space-y-4">
              {content.animation.cards.map((card_item, i) => (
                <div key={i} className={`rounded-lg p-4 transition-opacity ${dark ? 'bg-[#0D1B2A]' : 'bg-gray-50'} ${card_item.enabled ? 'opacity-100' : 'opacity-60'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--accent)' }}>Card {i + 1}</span>
                      <span className={`text-[11px] font-semibold ${card_item.enabled ? 'text-green-500' : muted}`}>
                        {card_item.enabled ? 'Visible' : 'Hidden'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => persistAnimationCardToggle(i)}
                        className="relative w-11 h-6 rounded-full transition-colors cursor-pointer"
                        style={{ backgroundColor: card_item.enabled ? 'var(--accent)' : dark ? 'rgba(255,255,255,0.2)' : '#e5e7eb' }}
                        title={card_item.enabled ? 'Hide card' : 'Show card'}
                      >
                        <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${card_item.enabled ? 'left-6' : 'left-1'}`}></span>
                      </button>
                      <button onClick={() => removeAnimCard(i)} className="w-7 h-7 flex items-center justify-center rounded-lg text-red-400 hover:bg-red-500/10 cursor-pointer">
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className={labelCls}>Icon (Remix Icon)</label>
                      <div className="flex items-center gap-2">
                        <input className={`${fieldCls} flex-1`} value={card_item.icon} onChange={(e) => updateAnimCard(i, 'icon', e.target.value)} placeholder="ri-flashlight-line" />
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg shrink-0" style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 20%, transparent)' }}>
                          <i className={`${card_item.icon} text-lg`} style={{ color: 'var(--accent)' }}></i>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Title</label>
                      <input className={fieldCls} value={card_item.title} onChange={(e) => updateAnimCard(i, 'title', e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Description</label>
                    <input className={fieldCls} value={card_item.desc} onChange={(e) => updateAnimCard(i, 'desc', e.target.value)} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Preview note */}
          <div className={`rounded-lg p-4 border ${dark ? 'border-white/10 bg-[#0D1B2A]/60' : 'border-gray-100 bg-gray-50'}`}>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0" style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 15%, transparent)' }}>
                <i className="ri-information-line text-sm" style={{ color: 'var(--accent)' }}></i>
              </div>
              <div>
                <p className={`text-xs font-semibold ${text}`}>Animation is code-generated</p>
                <p className={`text-xs ${muted}`}>Animasyon gövdesini, kart grubunu ve her kartı ayrı ayrı kontrol edebilirsiniz. Metin düzenlemeleri Save ile kalıcı olur; toggle değişimleri sayfaya anında yansır.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Button for animation tab */}
      {tab === 'animation' && (
        <div className="flex items-center justify-end gap-3 pt-2">
          {saved && (
            <div className="flex items-center gap-2 text-green-400 text-sm font-semibold">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-check-line"></i>
              </div>
              Saved &amp; published to site!
            </div>
          )}
          <button
            onClick={handleSave}
            className="flex items-center gap-2 text-white font-bold px-6 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm"
            style={{ backgroundColor: 'var(--accent)' }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-save-line"></i>
            </div>
            Save &amp; Publish
          </button>
        </div>
      )}

      {/* Save Button (for other tabs) */}
      {tab !== 'launch' && tab !== 'wiki' && tab !== 'legal' && tab !== 'faq' && tab !== 'animation' && (
        <div className="flex items-center justify-between gap-3 pt-2">
          <button
            onClick={handleReset}
            className={`flex items-center gap-2 font-bold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm border ${dark ? 'border-white/10 text-white/40 hover:text-white/70 hover:border-white/20' : 'border-gray-200 text-gray-400 hover:text-gray-600'}`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-refresh-line"></i>
            </div>
            Reset to Default
          </button>
          <div className="flex items-center gap-3">
            {saved && (
              <div className="flex items-center gap-2 text-green-400 text-sm font-semibold">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-check-line"></i>
                </div>
                Saved &amp; published to site!
              </div>
            )}
            <button
              onClick={handleSave}
              className="flex items-center gap-2 bg-[#F97316] text-white font-bold px-6 py-2.5 rounded-lg hover:bg-[#ea6a0a] transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-save-line"></i>
              </div>
              Save &amp; Publish
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminContentPage() {
  return (
    <AdminLayout title="Content Management">
      <ContentManagementInner />
    </AdminLayout>
  );
}
